import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

export interface Catalogue {
  id: string
  name: string
  slug?: string
  description?: string
  is_active: boolean
  is_published?: boolean
  is_featured?: boolean
  visibility_rules?: Record<string, any>
  created_at: string
  updated_at: string
  product_count?: number
  collection_count?: number
}

export interface CatalogueCreate {
  name: string
  description?: string
  is_active?: boolean
  is_published?: boolean
  is_featured?: boolean
  visibility_rules?: Record<string, any>
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  pages: number
}

export const catalogueApi = createApi({
  reducerPath: 'catalogueApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Catalogue'],
  endpoints: (builder) => ({
    getCatalogues: builder.query<PaginatedResponse<Catalogue>, {
      page?: number
      per_page?: number
      search?: string
    }>({
      query: (params) => ({
        url: '/catalogues',
        params,
      }),
      providesTags: ['Catalogue'],
    }),
    getCatalogue: builder.query<Catalogue, string>({
      query: (id) => `/catalogues/${id}`,
      providesTags: ['Catalogue'],
    }),
    createCatalogue: builder.mutation<Catalogue, CatalogueCreate>({
      query: (data) => ({
        url: '/catalogues',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Catalogue'],
    }),
    updateCatalogue: builder.mutation<Catalogue, { id: string; data: Partial<CatalogueCreate> }>({
      query: ({ id, data }) => ({
        url: `/catalogues/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Catalogue'],
    }),
    deleteCatalogue: builder.mutation<void, string>({
      query: (id) => ({
        url: `/catalogues/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Catalogue'],
    }),
  }),
})

export const {
  useGetCataloguesQuery,
  useGetCatalogueQuery,
  useCreateCatalogueMutation,
  useUpdateCatalogueMutation,
  useDeleteCatalogueMutation,
} = catalogueApi
